import { Logger } from 'opensolar-sdk'
import { SystemConditionDef } from './condition'
import { formatConditionFailureReport } from './designRulesConditionLog'
import { ConditionEvalRecord, SystemContext } from './types'

export type { ConditionEvalRecord }

const logger = new Logger('OS.DesignRules', { color: '#7C3AED' })
logger.log_stack = false

const DEBUG_STORAGE_KEY = 'debugDesignRules'
const VERBOSE_STORAGE_KEY = 'debugDesignRulesVerbose'

function isTruthyStorageValue(value: string | null): boolean {
  return value === 'true' || value === '1'
}

function readStorageFlag(key: string): boolean {
  try {
    return isTruthyStorageValue(localStorage.getItem(key))
  } catch {
    return false
  }
}

function applyDesignRulesLogLevel(): void {
  logger.explicit_level = readStorageFlag(DEBUG_STORAGE_KEY) ? 'debug' : undefined
}

function isVerbose(): boolean {
  return readStorageFlag(VERBOSE_STORAGE_KEY)
}

export function isDesignRulesDebugEnabled(): boolean {
  return readStorageFlag(DEBUG_STORAGE_KEY)
}

/** Compact trigger/context fields for console output (avoids serializing Redux, project, system). */
export function formatDesignRulesContextSnapshot(context: SystemContext): Record<string, unknown> {
  const trigger = context.trigger
  return {
    systemRuleKey: context.systemRuleKey,
    systemUuid: context.system?.uuid,
    trigger: trigger
      ? {
          initial: trigger.initial,
          studioSignal: trigger.studioSignal,
          projectField: trigger.projectField,
          statePathsChanged: trigger.statePathsChanged,
          studioItemType: trigger.studioItem?.type,
          studioItemUuid: trigger.studioItem?.uuid,
        }
      : undefined,
    orgId: context.org?.id,
    projectId: context.project?.id,
    isLite: context.project?.is_lite,
    workflowStageId: context.project?.workflow_stage_id,
    projectSold: context.project?.project_sold,
    roleAdmin: context.role?.is_admin,
  }
}

export function logDesignRulesCondition(ret: boolean, def: SystemConditionDef, context: SystemContext): void {
  applyDesignRulesLogLevel()
  if (!isVerbose()) return
  logger.debug('Checking condition:', ret, def, context)
}

export function logDesignRulesRuleOutcome(
  outcome: 'PASSED' | 'NOT PASSED' | 'Suppressing effects',
  ruleId: string,
  context: SystemContext,
  conditionLogs?: ConditionEvalRecord[]
): void {
  applyDesignRulesLogLevel()

  if (outcome === 'NOT PASSED' && conditionLogs?.length) {
    logger.groupCollapsed(`NOT PASSED '${ruleId}'`, () => {
      logger.debug('Context:', isVerbose() ? context : formatDesignRulesContextSnapshot(context))
      logger.debug('Failed conditions:\n' + formatConditionFailureReport(conditionLogs))
    })
    return
  }

  const payload = isVerbose() ? context : formatDesignRulesContextSnapshot(context)
  logger.debug(`${outcome} '${ruleId}':`, payload)
}

export function logDesignRulesEffect(def: unknown): void {
  applyDesignRulesLogLevel()
  logger.debug('Executing effect:', def)
}

export function logDesignRules(...args: unknown[]): void {
  applyDesignRulesLogLevel()
  logger.debug(...args)
}

export function logDesignRulesInfo(...args: unknown[]): void {
  applyDesignRulesLogLevel()
  logger.info(...args)
}

export function logDesignRulesWarn(...args: unknown[]): void {
  applyDesignRulesLogLevel()
  logger.warn(...args)
}
